import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useForm } from 'react-hook-form';

import { SelectedItemsFromLocalStorage } from '@/components/contextMenu/mwdContextMenu/types';
import { getMutatieErrorMessage, isMutatieSucces } from '@/queries/mutaties/dialogMutatieResponseUtils';
import { VersterkInzettenOpdracht } from '@/queries/mutaties/opdrachten';
import { useMutaties } from '@/queries/mutaties/useMutaties';
import { mapArrayOfInzetPositiesOpTeVersterkenTrein } from '@/queries/queryModels/inzetPositiesOpTeVersterkenTreinModel';
import { mapValidatieVoorEenheidNummer } from '@/queries/queryModels/validatieVoorEenheidNummerModel';
import { useGetQuery } from '@/queries/useGetQuery';
import { identity } from '@/utils/functions';

export const useMultiVersterkenDialog = (
  details: SelectedItemsFromLocalStorage[],
  handleOpenChange: (open: boolean) => void,
  showDialog: boolean,
) => {
  const { versterkInzetten } = useMutaties();
  const { handleSubmit, clearErrors, control, setError, reset, formState } = useForm({
    mode: 'onSubmit',
  });
  // sorteer details op vertrektijd
  const detailsSortedOnTime = details;
  // get alle eenheidnummers
  const { data: alleEenheidNummersData, fetchData: fetchAlleEenheidNummersData } = useGetQuery(identity);
  // valideer eenheidnummer
  const {
    data: validatieOpEenheidNummerData,
    fetchData: fetchValidatieOpEenheidNummerData,
    hasBeenCalled: fetchValidatieOpEenheidHasBeenCalled,
  } = useGetQuery(mapValidatieVoorEenheidNummer);
  // get geselecteerde datum van de eerste inzet
  const selectedDate = format(new Date(detailsSortedOnTime[0].vertrekTijd), 'yyyy-MM-dd');

  const [errorMessage, setErrorMessage] = useState<string>('');

  const [eenheidNummerData, setEenheidNummerData] = useState<{
    materieelSoort?: string;
    materieelAanduiding?: string;
    werklijnId?: string;
    werklijnVersie?: number;
  }>({});

  const resetDialog = () => {
    clearErrors();
    reset();
    setErrorMessage('');
    clearInzetPositieData();
    handleOpenChange(false);
  };

  // get inzetPosities voor te versterken trein
  const {
    data: inzetPositieData,
    fetchData: fetchInzetPositieData,
    clearData: clearInzetPositieData,
  } = useGetQuery(mapArrayOfInzetPositiesOpTeVersterkenTrein);

  // map inzetPositieData naar de opties in de inzetPositie dropdown
  const inzetPosities = useMemo(() => {
    return inzetPositieData?.map((item) => ({
      value: item.positie.toString(),
      label: item.volledigeNaam,
    }));
  }, [inzetPositieData]);

  const huidigEenheidNummer = details[0]?.stelNummer?.split(' ')[0];

  // valideer eenheidnummer als er een eenheidnummer is geselecteerd
  const handleEenheidNummerChange = async (eenheidNummer: string) => {
    if (eenheidNummer === huidigEenheidNummer) {
      setError('eenheid', {
        type: 'manual',
        message: 'Je kunt niet versterken met de eigen eenheid',
      });
      return;
    }
    await fetchValidatieOpEenheidNummerData(`/werklijnen/versterk-extract/${eenheidNummer}`);
  };

  useEffect(() => {
    if (showDialog) {
      fetchInzetPositieData(
        `/inzet/bepaal-posities/${detailsSortedOnTime[0].treinnummer}/${selectedDate}/${detailsSortedOnTime[0].dienstregelpuntVan}`,
      );
      fetchAlleEenheidNummersData('/stamgegevens/eenheidnummers');
    } else {
      resetDialog();
    }
  }, [showDialog, detailsSortedOnTime, selectedDate]);

  useEffect(() => {
    if (validatieOpEenheidNummerData) {
      clearErrors();
      setEenheidNummerData({
        materieelSoort: validatieOpEenheidNummerData?.eenheidSoort,
        materieelAanduiding: validatieOpEenheidNummerData?.eenheidAanduiding,
        werklijnId: validatieOpEenheidNummerData?.betrokkenWerklijn?.awbId,
        werklijnVersie: validatieOpEenheidNummerData?.betrokkenWerklijn?.versie,
      });
    } else if (validatieOpEenheidNummerData === null && fetchValidatieOpEenheidHasBeenCalled) {
      setError('eenheid', {
        type: 'manual',
        message: 'Dit materieelnummer bestaat niet',
      });
    } else if (validatieOpEenheidNummerData === undefined) {
      clearErrors();
    }

    // only run when validatieOpEenheid is fetched
  }, [clearErrors, validatieOpEenheidNummerData]);

  // map alle eenheidnummers to options in de teVersterkenTrein dropdown, exclusief het huidige eenheidnummer
  const teVersterkenEenheidNummers = useMemo(() => {
    return alleEenheidNummersData
      ?.filter((item) => item !== huidigEenheidNummer)
      .map((item) => ({
        value: item,
        label: item,
      }));
  }, [alleEenheidNummersData, huidigEenheidNummer]);

  const onSubmit = async (formData) => {
    const versterkMeerdereInzettenOpdracht: VersterkInzettenOpdracht = {
      eersteTeVersterkenInzetId: detailsSortedOnTime[0].selectedId,
      laatsteTeVersterkenInzetId: detailsSortedOnTime[detailsSortedOnTime.length - 1].selectedId,
      naInzetId: null,
      gewensteInzetPositie: formData.inzetPositie,
      teVersterkenWerklijn: {
        id: detailsSortedOnTime[0].werklijnId,
        versie: detailsSortedOnTime[0].versieId,
      },
      versterkenMetWerklijn: {
        id: eenheidNummerData.werklijnId,
        versie: eenheidNummerData.werklijnVersie,
      },
      materieelType: {
        soort: eenheidNummerData.materieelSoort,
        aanduiding: eenheidNummerData.materieelAanduiding,
      },
    };
    await (async () => {
      const result = await versterkInzetten(versterkMeerdereInzettenOpdracht);
      if (isMutatieSucces(result)) {
        resetDialog();
      } else {
        const error = getMutatieErrorMessage(result);
        if (error) {
          setErrorMessage(error);
        }
      }
    })();
  };

  const hasErrors = Object.keys(formState.errors).length > 0;

  return {
    handleSubmit,
    clearErrors,
    control,
    detailsSortedOnTime,
    onSubmit,
    resetDialog,
    inzetPosities,
    handleEenheidNummerChange,
    teVersterkenEenheidNummers,
    hasErrors,
    errorMessage,
  };
};
